import {
  AbsoluteCenter,
  Badge,
  Box,
  Card,
  CardBody,
  Divider,
  FormControl,
  FormLabel,
  HStack,
  IconButton,
  Input,
  InputGroup,
  InputRightElement,
  Link,
  Stack,
  Text,
  Tooltip,
} from '@chakra-ui/react';
import PropTypes from 'prop-types';
import { isArrayEmpty, isArrayValue } from '@/utils/helpers.js';
import { FiCopy, FiLink2 } from 'react-icons/fi';
import { Fragment, useState } from 'react';
import { FaCopy } from 'react-icons/fa6';
import { Virtuoso } from 'react-virtuoso';
import { addYears, format, isValid, parseISO } from 'date-fns';
import { useSelector } from 'react-redux';
import { Link as RouterLink } from 'react-router-dom';
import { selectEntityType } from '@/features/enum/enumSlice.js';

const colors = ['orange', 'red', 'green', 'purple', 'cyan'];

/**
 * Every scalar below here becomes a labelled input with its own copy tooltip, so a checklist's
 * several hundred skills mount tens of thousands of nodes at once. Longer arrays window their rows.
 */
export const VIRTUALIZE_ITEM_LIMIT = 25;

const VIRTUAL_LIST_HEIGHT = '600px';

const singularize = (name) => {
  if (name.endsWith('ies')) {
    return `${name.slice(0, -3)}y`;
  }
  return name.endsWith('s') ? name.slice(0, -1) : name;
};

/** Fields named for the role an entity plays, which their name alone cannot tie to a type. */
const ENTITY_TYPE_ALIASES = {
  recruiter: 'user',
  sales_rep: 'user',
  compliance_specialist: 'user',
  facility: 'company',
  billing_customer: 'company',
  extensions: 'engagement',
};

/** The entity type a nested field holds, read from its name: `job`, `parent_engagement`, `modalities`. */
export const linkedEntityType = (fieldName, entityTypes) => {
  const knownTypes = Object.values(entityTypes ?? {});
  const alias = ENTITY_TYPE_ALIASES[fieldName];
  if (alias) {
    return knownTypes.includes(alias) ? alias : null;
  }
  for (const name of new Set([fieldName, singularize(fieldName)])) {
    if (knownTypes.includes(name)) {
      return name;
    }
    const suffixMatch = knownTypes.filter((type) => name.endsWith(`_${type}`)).sort((a, b) => b.length - a.length)[0];
    if (suffixMatch) {
      return suffixMatch;
    }
  }
  return null;
};

export const entityLookupPath = (entityType, nucleusId) =>
  `/entity_lookup?${new URLSearchParams({ source: 'nucleus', entity: entityType, id: nucleusId })}`;

const useNestedEntityPath = (fieldName, value) => {
  const entityType = linkedEntityType(fieldName, useSelector(selectEntityType));
  return entityType && value?.id ? entityLookupPath(entityType, value.id) : null;
};

const NestedEntityLink = ({ fieldName, value, children }) => {
  const path = useNestedEntityPath(fieldName, value);

  if (!path) {
    return children;
  }

  return (
    <Link as={RouterLink} to={path} color={'brand.700'} textDecoration={'underline'}>
      {children}
    </Link>
  );
};

NestedEntityLink.propTypes = {
  fieldName: PropTypes.string,
  value: PropTypes.any,
  children: PropTypes.node,
};

const DataHeading = ({ label, empty, bg, fieldName, value }) => {
  return (
    <Box pos="relative" width="100%" p={2} key={label}>
      <Divider />
      <AbsoluteCenter bg={bg} px={4}>
        <i>
          {fieldName ? (
            <NestedEntityLink fieldName={fieldName} value={value}>
              {label}
            </NestedEntityLink>
          ) : (
            label
          )}{' '}
        </i>
        {empty && <Badge colorScheme={bg?.split('.')[0]}>Empty</Badge>}
      </AbsoluteCenter>
    </Box>
  );
};

DataHeading.propTypes = {
  label: PropTypes.string,
  empty: PropTypes.bool,
  bg: PropTypes.string,
  fieldName: PropTypes.string,
  value: PropTypes.any,
};

const ReadOnlyField = ({ heading, label, value }) => {
  const [copySuccess, setCopySuccess] = useState(false);

  const handleCopy = () => {
    navigator.clipboard.writeText(value).then(() => {
      setCopySuccess(true);
      setTimeout(() => {
        setCopySuccess(false);
      }, 1000);
    });
  };

  return (
    <FormControl mt={2} ml={heading ? 8 : 0} key={label + value}>
      <FormLabel>{label}</FormLabel>
      <InputGroup>
        <Input
          isReadOnly
          type="text"
          value={value || 'null'}
          placeholder={!value ? 'null' : ''}
          bgColor={'white'}
          color={'gray.500'}
          _placeholder={{ color: 'brand.500' }}
        />
        <InputRightElement>
          <Tooltip hasArrow placement="top" label={copySuccess ? 'Copied!' : 'Copy'}>
            <IconButton
              size={'sm'}
              variant={'ghost'}
              color={copySuccess ? 'green.500' : 'gray.300'}
              aria-label="copy"
              icon={<FaCopy />}
              onClick={handleCopy}
            />
          </Tooltip>
        </InputRightElement>
      </InputGroup>
    </FormControl>
  );
};

ReadOnlyField.propTypes = {
  heading: PropTypes.string,
  label: PropTypes.string,
  value: PropTypes.any,
};

/** A skills checklist stays good for a year from the day it was completed. */
export const expiresOn = (completedOn) => {
  if (!completedOn || !isValid(parseISO(completedOn))) {
    return null;
  }

  // Expiration is a calendar date, so it counts from the date as written. Counting from the
  // instant would shift the displayed day by one either side of UTC.
  const [completedDate] = completedOn.split(/[T ]/);
  return format(addYears(parseISO(completedDate), 1), 'yyyy-MM-dd');
};

const EntityListItem = ({ fieldName, item, index }) => {
  const path = useNestedEntityPath(fieldName, item);

  return (
    <Box p={2}>
      {path && (
        <Link as={RouterLink} to={path} fontSize={'sm'} fontStyle={'italic'} color={'brand.700'} textDecoration={'underline'}>
          {`${fieldName} ${index}`}
        </Link>
      )}
      {entityResultParser(item, true)}
    </Box>
  );
};

EntityListItem.propTypes = {
  fieldName: PropTypes.string,
  item: PropTypes.any,
  index: PropTypes.number,
};

const VirtualizedEntityList = ({ items, label }) => (
  <Virtuoso
    data={items}
    aria-label={`${label} rows`}
    style={{ height: VIRTUAL_LIST_HEIGHT, width: '100%' }}
    itemContent={(index, item) => <EntityListItem fieldName={label} item={item} index={index} />}
  />
);

VirtualizedEntityList.propTypes = {
  items: PropTypes.array,
  label: PropTypes.string,
};

export const entityResultParser = (entity, isNested = false) => {
  const formattedContentBg = isNested ? 'yellow.100' : 'blue.50';
  if (entity && typeof entity === 'object') {
    return Object.entries(entity).map(([key, value]) => {
      if (key === 'audit_logs' || key === 'system_ids') {
        return null;
      } else if (key === 'contract') {
        return (
          <Box key={value?.pk ?? key} p={3} bg={formattedContentBg} width={'100%'} borderRadius={6} mt={4}>
            <DataHeading bg={formattedContentBg} label={key} empty={!value?.pk} />
            {value?.pk && (
              <HStack>
                <Box>{value?.name ?? 'null'}</Box>
                <Link aria-label="view contract" href={`/contracts/${value?.pk ?? ''}/details`}>
                  <IconButton variant="outline" icon={<FiLink2 />} />
                </Link>
              </HStack>
            )}
          </Box>
        );
      } else if (key === 'completed_on') {
        return (
          <Fragment key={key}>
            <ReadOnlyField label={key} value={value} />
            <ReadOnlyField label="expires_on" value={expiresOn(value)} />
          </Fragment>
        );
      } else if (key === 'state') {
        return (
          <Box key={value?.id ?? key} p={3} bg={formattedContentBg} width={'100%'} borderRadius={6} mt={4}>
            <ReadOnlyField label={key} value={value?.name ?? 'null'} />
          </Box>
        );
      } else if (key === 'modality' || key === 'primary_modality') {
        return (
          <Box key={value?.id ?? key} p={3} bg={formattedContentBg} width={'100%'} borderRadius={6} mt={4}>
            <ReadOnlyField label={key} value={value?.short_name ?? 'null'} />
          </Box>
        );
      } else if (key === 'matched_pay_rule') {
        return (
          <Box key={value?.pk ?? key} p={3} bg={formattedContentBg} width={'100%'} borderRadius={6} mt={4}>
            <ReadOnlyField label={key} value={value?.name ?? 'null'} />
          </Box>
        );
      } else if (key === 'sales_rep' || key === 'recruiter') {
        return (
          <Box key={(value?.pk || value?.id) ?? key} p={3} bg={formattedContentBg} width={'100%'} borderRadius={6} mt={4}>
            <DataHeading bg={formattedContentBg} label={key} fieldName={key} value={value} />
            {value?.first_name ? (
              <Stack spacing={1} alignItems={'start'}>
                <HStack>
                  <Text>{value?.first_name + ' ' + value?.last_name}</Text>
                </HStack>
                <Text pt={2} fontSize={'sm'} opacity={0.5}>
                  {value?.email}
                </Text>
              </Stack>
            ) : (
              <ReadOnlyField label={key} value={'null'} />
            )}
          </Box>
        );
      } else if (key === 'facility' || key === 'billing_customer') {
        const activeId = isArrayValue(value?.system_ids) ? value.system_ids.find((id) => id.name === 'bullhorn' && id.is_active) : null;
        return (
          <Box key={value?.id ?? key} p={3} bg={formattedContentBg} width={'100%'} borderRadius={6} mt={4}>
            <DataHeading bg={formattedContentBg} label={key} empty={!value?.id} fieldName={key} value={value} />
            {value?.id && (
              <Card p={0} bg={formattedContentBg} width={'100%'} borderRadius={6} mt={1} shadow={'none'} border={'none'}>
                <CardBody>
                  <Stack spacing={1} alignItems={'start'}>
                    <HStack>
                      <Text>{value?.name}</Text>
                      {activeId && (
                        <Link aria-label={`view ${key}`} href={`/entity_lookup?source=bullhorn&entity=company&id=${activeId.system_id}`}>
                          <IconButton variant="outline" icon={<FiLink2 />} />
                        </Link>
                      )}
                    </HStack>
                    <Text pt={2} fontSize={'sm'}>
                      {`${value?.address}, ${value?.city}, ${value?.state?.name}`}
                    </Text>
                    <Text pt={2} fontSize={'sm'} opacity={0.5}>
                      {`Type: ${value?.facility_type}`}
                    </Text>
                  </Stack>
                </CardBody>
              </Card>
            )}
          </Box>
        );
      }
      if (isArrayValue(value)) {
        const colorIndex = key.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0) % colors.length;
        const arrayBg = `${colors[colorIndex]}.50`;
        return (
          <Box key={key} mt={4} p={3} bg={arrayBg} width={'100%'} borderRadius={6}>
            <DataHeading bg={arrayBg} label={key + ` [${value.length}]`} />
            <Box mt={2}>
              {value.length > VIRTUALIZE_ITEM_LIMIT ? (
                <VirtualizedEntityList items={value} label={key} />
              ) : (
                value.map((val, i) => <EntityListItem key={`${key} ${i}`} fieldName={key} item={val} index={i} />)
              )}
            </Box>
          </Box>
        );
      } else if (value && typeof value === 'object') {
        return (
          <Box key={key} p={3} bg={'yellow.50'} width={'100%'} borderRadius={6} mt={4}>
            <DataHeading bg={'yellow.50'} label={key} empty={isArrayEmpty(value)} fieldName={key} value={value} />
            {entityResultParser(value, true)}
          </Box>
        );
      } else {
        return <ReadOnlyField key={key} label={key} value={value} />;
      }
    });
  } else if (typeof entity === 'string') {
    return entity;
  }
};

entityResultParser.propTypes = {
  entity: PropTypes.object,
};
